import React, { useCallback, useEffect, useState } from 'react';
import { listCameraEvents } from '../../services/cameras';
import { formatInTimezone } from '../../utils/formatInTimezone';
import { formatDuration } from '../../utils/formatDuration';
import { countRu, ATTEMPT_FORMS } from '../../utils/pluralRu';
import './CameraEventHistory.css';

const PAGE_SIZE = 25;

const FILTERS = [
  { key: 'all', label: 'Все', category: null },
  { key: 'incident', label: 'Аварии', category: 'incident' },
  { key: 'retry', label: 'Retry', category: 'retry' },
  { key: 'restored', label: 'Восстановление', category: 'restored' },
  { key: 'manual', label: 'Ручные действия', category: 'manual' },
];

const normalizeEvents = (data) => {
  if (Array.isArray(data)) return { items: data, total: data.length };
  if (data && Array.isArray(data.items)) return { items: data.items, total: data.total ?? data.items.length };
  if (data && Array.isArray(data.results)) return { items: data.results, total: data.total ?? data.results.length };
  return { items: [], total: 0 };
};

// Backend относит connection_restored к event_category=incident, поэтому фильтр
// «Аварии» нельзя строить только по event_category. Исключаем recovery-события
// (event_code содержит restored/recovered или сама категория restored), иначе
// «Аварии» показывают то же, что «Восстановление».
const isRecoveryEvent = (event) => {
  const code = (event.event_code || '').toLowerCase();
  const cat = (event.event_category || '').toLowerCase();
  return cat === 'restored' || code.includes('restored') || code.includes('recovered');
};

const matchesFilter = (event, category) => {
  if (!category) return true;
  const cat = (event.event_category || '').toLowerCase();
  if (category === 'restored') return cat === 'restored' || (event.event_code || '').includes('restored');
  if (category === 'incident') return cat === 'incident' && !isRecoveryEvent(event);
  return cat === category;
};

const CameraEventHistory = ({ cameraId, displayTimezone, refreshKey = 0 }) => {
  const [events, setEvents] = useState([]);
  const [hasNext, setHasNext] = useState(false);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback((pageIndex) => {
    setLoading(true);
    setError('');
    listCameraEvents(cameraId, { limit: PAGE_SIZE, offset: pageIndex * PAGE_SIZE })
      .then((data) => {
        const { items } = normalizeEvents(data);
        setEvents(items);
        setHasNext(items.length === PAGE_SIZE);
      })
      .catch(() => setError('Не удалось загрузить историю событий'))
      .finally(() => setLoading(false));
  }, [cameraId]);

  useEffect(() => { setPage(0); load(0); }, [load, refreshKey]);

  const activeCategory = FILTERS.find((f) => f.key === filter)?.category ?? null;
  const visible = events.filter((e) => matchesFilter(e, activeCategory));

  const goTo = (p) => { setPage(p); load(p); };

  return (
    <div className="camera-event-history">
      <div className="event-filters mb-3" role="group">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`btn btn-sm btn-outline-secondary ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>


      {loading && <div className="text-muted">Загрузка…</div>}
      {error && <div className="text-danger">{error}</div>}
      {!loading && !error && visible.length === 0 && <div className="text-muted">Событий нет</div>}

      <ul className="event-list list-unstyled">
        {visible.map((e) => {
          const isRetry = (e.event_category || '').toLowerCase() === 'retry' || (e.event_code || '') === 'retry_attempt';
          return (
            <li key={e.id} className={`event-row ${isRetry ? 'event-row-compact small text-muted' : 'mb-2'}`}>
              <span className="event-time me-2">{formatInTimezone(e.created_at, displayTimezone)}</span>
              <span className="event-message">{e.message || e.event_code}</span>
              {e.retry_count != null && <span className="ms-2">· {countRu(e.retry_count, ATTEMPT_FORMS)}</span>}
              {e.incident_duration_seconds != null && (
                <span className="ms-2">· {formatDuration(e.incident_duration_seconds)}</span>
              )}
            </li>
          );
        })}
      </ul>

      <div className="event-pagination d-flex gap-2 align-items-center">
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 0} onClick={() => goTo(page - 1)}>Назад</button>
        <span className="small">Стр. {page + 1}</span>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!hasNext} onClick={() => goTo(page + 1)}>Вперёд</button>
      </div>
    </div>
  );
};

export default CameraEventHistory;
